/**
 * Shared TVOD product map + nav state helper.
 *
 * The CRM landing_page tvod category returns product stubs with only IDs,
 * but movie detection against UIDs is required — full product UIDs come from
 * the user TV Store endpoint (/users/{id}/tv_store).
 *
 * This module caches the UID→product Map after the first fetch, so pages
 * don't need to refetch it. Call getTvodProductMap() from any page that
 * navigates to MovieDetailsPage.
 */

import { getClientApiUrl } from "../config/apiConfig";

let cachedTvodMap = null;
let pendingPromise = null;

/**
 * Fetch (once) and cache the TVOD product map: UID → full product object.
 * Requires `tvStoreCategories` to be already loaded in Redux.
 *
 * @param {Array} tvStoreCategories — state.content.tvStoreCategories
 * @returns {Promise<Map>} — Map<uid, product>
 */
export const getTvodProductMap = async (tvStoreCategories) => {
  if (cachedTvodMap) return cachedTvodMap;
  if (pendingPromise) return pendingPromise;

  pendingPromise = (async () => {
    const tvodCategory = tvStoreCategories?.find((c) => c.uid === "tvod");
    const tvodProductIds = new Set(tvodCategory?.tv_store?.map((p) => p.id) || []);
    const map = new Map();
    if (tvodProductIds.size === 0) {
      cachedTvodMap = map;
      return map;
    }
    try {
      const accessToken = localStorage.getItem("mango_access_token");
      const userId = localStorage.getItem("mango_user_id");
      const res = await fetch(`${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/tv_store`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (data.status === "ok") {
        data.data?.forEach((p) => { if (tvodProductIds.has(p.id)) map.set(p.uid, p); });
      }
    } catch (e) {
      console.warn("[tvodMap] Failed to fetch tv_store:", e.message);
    }
    cachedTvodMap = map;
    console.log("[tvodMap] Loaded", map.size, "TVOD products");
    return map;
  })();

  return pendingPromise;
};

/**
 * Synchronous accessor — returns the cached map or null if not yet fetched.
 */
export const getCachedTvodMap = () => cachedTvodMap;

/**
 * Build MovieDetailsPage nav state for a movie item.
 * - If the movie is TVOD: returns full rental state (fromTab: "Rent", price, etc.)
 * - Otherwise: returns { fromTab: "Discover" } or {} if no tab context.
 *
 * Matches by package UID first, falls back to movie's own uid (TVOD products
 * share the same uid as the rental movie, e.g. "theescort", "bonnyclara").
 *
 * @param {Object} item — movie item (may have packages + uid)
 * @param {Map} tvodByUid — map from getTvodProductMap()
 * @param {string} [defaultTab="Discover"] — tab to use when not TVOD
 */
export const buildMovieDetailNavState = (item, tvodByUid, defaultTab = "Discover") => {
  // Content kind hint for the /details/:id dispatcher (movie vs series).
  const contentType = item?.type || (item?.series_id || item?.seasons ? "series" : "movie");
  const base = defaultTab ? { fromTab: defaultTab, contentType } : { contentType };
  if (!tvodByUid || tvodByUid.size === 0) return base;
  const matchedPkgUid = item?.packages?.find((pkg) => tvodByUid.has(pkg.uid))?.uid;
  const tvodProduct = matchedPkgUid
    ? tvodByUid.get(matchedPkgUid)
    : (item?.uid && tvodByUid.has(item.uid) ? tvodByUid.get(item.uid) : null);
  if (tvodProduct) {
    return {
      contentType,
      fromTab: "Rent",
      fromRent: true,
      rentalPrice: tvodProduct.price_options?.[0]?.activation_price ?? null,
      tvStoreProductId: tvodProduct.id,
      rentalPurchased:
        (Array.isArray(tvodProduct.action) && tvodProduct.action.includes("DEACTIVATE")) ||
        !!tvodProduct.price_options?.some((po) => po.purchased),
    };
  }
  return base;
};
